/**
 * Canonical icon names for <Icon name="..." /> on mobile and web.
 * Maps legacy FontAwesome imports to canonical names during migration.
 *
 * Do NOT add logos, language flags, or nationality flags here.
 */

/** @type {readonly string[]} */
const ICON_NAMES = [
  'home',
  'home-alt',
  'house-user',
  'estate',
  'comment',
  'comments',
  'comments-alt',
  'comment-dots',
  'users',
  'users-alt',
  'infinity',
  'calendar',
  'calender',
  'game',
  'rocket',
  'arrow-left',
  'arrow-right',
  'arrow-up',
  'arrow-down',
  'bars',
  'filter',
  'bell',
  'search',
  'check',
  'check-circle',
  'check-square',
  'times',
  'plus',
  'setting',
  'sign-out',
  'heart',
  'award',
  'redo',
  'sync',
  'file-alt',
  'book-open',
  'life-ring',
  'shield',
  'shield-check',
  'user-plus',
  'user',
  'star',
  'exchange',
  'exchange-alt',
  'english-to-chinese',
  'map-marker',
  'angle-left',
  'angle-down',
  'angle-up',
  'lock',
  'medal',
  'schedule',
  'clock',
  'map',
  'building',
  'exclamation-circle',
  'report',
  'abacus',
  'spinner',
  'image',
  'hourglass',
  'ellipsis-h',
  'edit',
  'at',
  'globe',
  'ban',
  'pause-circle',
  'times-circle',
  'coins',
  'location-point',
  'comment-alt-lock',
  'envelope-check',
];

/**
 * FontAwesome icon export name → canonical Icon name.
 * Used when migrating imports file by file.
 */
const FONT_AWESOME_LEGACY = {
  faHouse: 'estate',
  faComment: 'comment',
  faComments: 'comments',
  faCommentDots: 'comment-dots',
  faUserGroup: 'users-alt',
  faUsers: 'users',
  faCalendar: 'calender',
  faGamepad: 'game',
  faArrowLeft: 'arrow-left',
  faArrowRight: 'arrow-right',
  faArrowUp: 'arrow-up',
  faArrowDown: 'arrow-down',
  faBars: 'bars',
  faMagnifyingGlass: 'search',
  faSearch: 'search',
  faCheck: 'check',
  faCheckCircle: 'check-circle',
  faSquareCheck: 'check-square',
  faXmark: 'times',
  faPlus: 'plus',
  faGear: 'setting',
  faSignOut: 'sign-out',
  faSignOutAlt: 'sign-out',
  faRightFromBracket: 'sign-out',
  faHeart: 'heart',
  faAward: 'award',
  faArrowsRotate: 'redo',
  faFileLines: 'file-alt',
  faShieldHalved: 'shield',
  faUserPlus: 'user-plus',
  faUser: 'user',
  faStar: 'star',
  faRightLeft: 'exchange',
  faArrowsAltH: 'exchange-alt',
  faMapPin: 'map-marker',
  faMapMarkerAlt: 'map-marker',
  faAngleLeft: 'angle-left',
  faChevronDown: 'angle-down',
  faChevronUp: 'angle-up',
  faLock: 'lock',
  faCertificate: 'medal',
  faCalendarPlus: 'schedule',
  faClock: 'clock',
  faMapLocation: 'map',
  faArchway: 'building',
  faCircleExclamation: 'exclamation-circle',
  faFlag: 'report',
  faCakeCandles: 'abacus',
  faSpinner: 'spinner',
  faImage: 'image',
};

/**
 * Canonical name → Unicons file slug (without uil- prefix uses uil-{slug}).
 */
const UNICON_SLUGS = {
  home: 'house-user',
  'home-alt': 'house-user',
  'house-user': 'house-user',
  estate: 'estate',
  comment: 'comment',
  comments: 'comments',
  'comments-alt': 'comments-alt',
  'comment-dots': 'comment-dots',
  users: 'users-alt',
  'users-alt': 'users-alt',
  calendar: 'calender',
  calender: 'calender',
  game: 'game-structure',
  rocket: 'rocket',
  'arrow-left': 'arrow-left',
  'arrow-right': 'arrow-right',
  'arrow-up': 'arrow-up',
  'arrow-down': 'arrow-down',
  bars: 'bars',
  filter: 'filter',
  search: 'search',
  check: 'check',
  'check-circle': 'check-circle',
  'check-square': 'check-square',
  times: 'times',
  plus: 'plus',
  setting: 'setting',
  'sign-out': 'sign-out-alt',
  heart: 'heart',
  award: 'award',
  redo: 'redo',
  sync: 'sync',
  premium: 'award',
  blog: 'book-open',
  support: 'life-ring',
  'book-open': 'book-open',
  'life-ring': 'life-ring',
  file: 'file-alt',
  'file-alt': 'file-alt',
  shield: 'shield',
  'shield-check': 'shield-check',
  'user-plus': 'user-plus',
  user: 'user',
  star: 'star',
  exchange: 'exchange',
  'exchange-alt': 'exchange-alt',
  'english-to-chinese': 'english-to-chinese',
  'map-marker': 'map-marker',
  'angle-left': 'angle-left',
  'angle-down': 'angle-down',
  'angle-up': 'angle-up',
  lock: 'lock',
  medal: 'medal',
  schedule: 'schedule',
  clock: 'clock',
  map: 'map',
  building: 'building',
  'exclamation-circle': 'exclamation-circle',
  report: 'exclamation-triangle',
  abacus: 'abacus',
  spinner: 'spinner',
  image: 'image',
  hourglass: 'hourglass',
  'ellipsis-h': 'ellipsis-h',
  edit: 'edit',
  at: 'at',
  globe: 'globe',
  ban: 'ban',
  'pause-circle': 'pause-circle',
  'times-circle': 'times-circle',
  coins: 'coins',
  'location-point': 'location-point',
  'comment-alt-lock': 'comment-alt-lock',
  'envelope-check': 'envelope-check',
};

function resolveIconName(name) {
  if (!name) return null;
  if (FONT_AWESOME_LEGACY[name]) return FONT_AWESOME_LEGACY[name];
  if (ICON_NAMES.includes(name)) return name;
  if (UNICON_SLUGS[name]) {
    const slug = UNICON_SLUGS[name];
    const canonical = ICON_NAMES.find((n) => UNICON_SLUGS[n] === slug);
    return canonical || name;
  }
  return null;
}

/**
 * FontAwesome icon definition iconName → canonical Icon name.
 * Used when migrating components that still pass FA icon objects.
 */
const FA_ICON_NAME = {
  house: 'estate',
  comment: 'comment',
  comments: 'comments',
  'comment-dots': 'comment-dots',
  'user-group': 'users-alt',
  users: 'users',
  calendar: 'calender',
  'calendar-plus': 'schedule',
  gamepad: 'game',
  'arrow-left': 'arrow-left',
  'arrow-right': 'arrow-right',
  'arrow-up': 'arrow-up',
  'arrow-down': 'arrow-down',
  bars: 'bars',
  'magnifying-glass': 'search',
  search: 'search',
  check: 'check',
  'check-circle': 'check-circle',
  'square-check': 'check-square',
  xmark: 'times',
  plus: 'plus',
  gear: 'setting',
  'sign-out-alt': 'sign-out',
  'right-from-bracket': 'sign-out',
  heart: 'heart',
  award: 'award',
  'arrows-rotate': 'redo',
  'file-lines': 'file-alt',
  'shield-halved': 'shield',
  'user-plus': 'user-plus',
  user: 'user',
  star: 'star',
  'right-left': 'exchange',
  'arrows-alt-h': 'exchange-alt',
  'map-pin': 'map-marker',
  'map-marker-alt': 'map-marker',
  'angle-left': 'angle-left',
  'chevron-down': 'angle-down',
  'chevron-up': 'angle-up',
  lock: 'lock',
  certificate: 'medal',
  clock: 'clock',
  'map-location': 'map',
  archway: 'building',
  'circle-exclamation': 'exclamation-circle',
  flag: 'report',
  'cake-candles': 'abacus',
  spinner: 'spinner',
  image: 'image',
};

/** Resolve Icon name from FA icon object, string, or legacy export key. */
function resolveIconFromProp(icon) {
  if (!icon) return null;
  if (typeof icon === 'string') return resolveIconName(icon);
  if (icon.prefix === 'fab') return null;
  if (icon.iconName && FA_ICON_NAME[icon.iconName]) {
    return FA_ICON_NAME[icon.iconName];
  }
  return null;
}

module.exports = {
  ICON_NAMES,
  FONT_AWESOME_LEGACY,
  UNICON_SLUGS,
  FA_ICON_NAME,
  resolveIconName,
  resolveIconFromProp,
};
